import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../../../components/Loader";
import BackButton from "../components/CustomFields/BackButton";
import BackgroundComponent from "../components/theme/BackgroundAnimation";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import InstitutionHeader from "../components/InstitutionHeader";
import { Pencil as EditIcon } from "lucide-react";

const InquiryInfo = () => {
  const navigate = useNavigate();
  const [inquiryData, setInquiryData] = useState(null);
  const [alert, setAlert] = useState({ message: "", type: "" });

  useEffect(() => {
    const storedData = localStorage.getItem("inquiryData");
    if (storedData) {
      setInquiryData(JSON.parse(storedData));
      setAlert({ message: "Enquiry sent successfully!", type: "success" });

      // Close the alert after 1 second
      setTimeout(() => {
        setAlert({ message: "", type: "" });
      }, 1000);
    } else {
      navigate("/sign-in");
    }
  }, [navigate]);

  if (!inquiryData) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 relative">
      {/* Header */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100 }}>
        <InstitutionHeader />
      </div>

      {/* Alert Message */}
      {alert.message && (
        <div
          className={`fixed top-5 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white flex items-center space-x-3 z-50 transition-all duration-300 ${alert.type === "success" ? "bg-green-500" : "bg-red-500"}`}
        >
          {alert.type === "success" ? (
            <AiOutlineCheckCircle className="text-xl" />
          ) : (
            <AiOutlineCloseCircle className="text-xl" />
          )}
          <span>{alert.message}</span>
        </div>
      )}

      {/* Background Animation */}
      <BackgroundComponent />

      {/* Back Button */}
      <div style={{ position: "fixed", top: "80px", left: "20px", zIndex: 90 }}>
        <BackButton />
      </div>

      <div className="relative z-10 flex items-center justify-center py-10" style={{ paddingTop: "120px" }}>
        <div className="w-full max-w-4xl mx-auto p-8 bg-white bg-opacity-90 shadow-xl rounded-lg">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-semibold">Enquiry Information</h2>
            <EditIcon
              className="h-5 w-5"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/enquiry?id=${inquiryData.primaryId}`)}
            />
          </div>
          <br></br>


          {/* Info message display */}
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
            <p className="font-bold">Review</p>
            <p>Please review the enquiry details.</p>
          </div>

          <div className="space-y-8">
            {/* Reference Number Section */}
            <div className="border border-gray-300 rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Reference Number
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <input
                  type="text"
                  value={inquiryData.primaryId}
                  readOnly
                  className="border bg-gray-100 p-3 rounded-lg w-full"
                />
              </div>
            </div>

            {/* Student Details Section */}
            <div className="border border-gray-300 rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Student Details
              </h3>
              <div className="grid grid-cols-2 gap-6">
                {/* Branch */}
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Branch:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.user.dynamicAttributes[2].value || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>

                {/* Student Name */}
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Student Name:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.user.firstName || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>

                {/* Gender */}
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Gender:
                  </label>
                  <input
                    type="text"
                    value={
                      inquiryData.user.gender
                        ? inquiryData.user.gender.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
                        : "N/A"
                    }
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>

                {/* Date of Birth */}
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Date of Birth:
                  </label>
                  <input
                    type="text"
                    value={
                      inquiryData.user.dob
                        ? new Date(inquiryData.user.dob).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                        : "N/A"
                    }
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>

                {/* Registration Class */}
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Registration Class:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.user.dynamicAttributes[1].value || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>

                {/* How Did You Know */}
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    How did you know about Velammal?:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.user.dynamicAttributes[0].value || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="border border-gray-300 rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Contact Details
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Relationship:
                  </label>
                  <input
                    type="text"
                    value={
                      inquiryData.user.contactPersonList[0].type
                        ? inquiryData.user.contactPersonList[0].type.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
                        : "N/A"
                    }
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Contact Person Name:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.user.contactPersonList[0].firstName || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Mobile Number:
                  </label>
                  <input
                    type="text"
                    value={
                      inquiryData.user.contactPersonList[0].phoneNumber
                        ? `+${inquiryData.user.contactPersonList[0].phoneNumber.slice(0, 2)} ${inquiryData.user.contactPersonList[0].phoneNumber.slice(2)}`
                        : "N/A"
                    }
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    Email Id:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.user.contactPersonList[0].email || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>
              </div>
            </div>

            {/* Previous School Details Section */}
            <div className="border border-gray-300 rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Previous School Details
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    School Name:
                  </label>
                  <input
                    type="text"
                    value={inquiryData.previousAffiliation.schoolName || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full"
                  />
                </div>
              </div>
            </div>

            {/* Query Information Section */}
            <div className="border border-gray-300 rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Query Information
              </h3>
              <div className="grid grid-cols-1 gap-6">
                <div className="flex flex-col">
                  <label className="font-semibold text-gray-700 mb-1">
                    {inquiryData.submittedDate
                      ? new Date(inquiryData.submittedDate).toLocaleString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      })
                      : "N/A"}

                  </label>
                  <textarea
                    value={inquiryData.query || "N/A"}
                    readOnly
                    className="border bg-gray-100 p-3 rounded-lg w-full resize-none"
                    rows={Math.min(10, (inquiryData.query || "").split("\n").length + 1)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryInfo;
